import React from "react";
import i18n from "../../../i18n/config";
import classnames from "classnames";
import styles from "./SidecarSettingsModal.module.scss";
import { observer, useLocalObservable } from "mobx-react-lite";
import { IModalInstance } from "@whatslly/ui-components/modals/ModalProvider";
import { BaseModal } from "@whatslly/ui-components/modals";
import { ClientSideSidecar } from "@whatslly/types/sidecars-server/Sidecar";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import isEqual from "lodash/isEqual";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { CheckBox } from "@whatslly/ui-components/v2/BaseElements/CheckBox";
import { useTranslation } from "react-i18next";
import type { ISidecarsStore } from "../ISidecarsStore";
import { InjectSidecarsStore } from "../ISidecarsStore";
interface FrequencyOption {
  title: string;
  value: {
    mode: "cron" | "permanent";
    verbose?: boolean;
    cron?: string;
  };
  monthlyPrice: number;
}
const frequencyOptions: FrequencyOption[] = [
  { title: "Every 12 hours", value: { mode: "cron", cron: "0 */12 * * *" }, monthlyPrice: 1 },
  { title: "Every 6 hours", value: { mode: "cron", cron: "0 */6 * * *" }, monthlyPrice: 2 },
  { title: "Every 3 hours", value: { mode: "cron", cron: "0 */3 * * *" }, monthlyPrice: 3 },
  { title: "Every hour", value: { mode: "cron", cron: "0 * * * *" }, monthlyPrice: 4 },
  { title: "Permanent", value: { mode: "permanent" }, monthlyPrice: 40 },
] as const;

type CheckboxValue = "on" | "off" | "partial";

type SidecarSettingsModalProp = {
  runMode?: ClientSideSidecar["runMode"];
  notifyEmail: CheckboxValue;
  autoReinvite: CheckboxValue;
  periodicReinvite: CheckboxValue;
  locale: string;
  hideFrequencyOptions?: boolean;
};

type SidecarSettingsModalResponse = {
  runMode: ClientSideSidecar["runMode"];
  notifyEmail: CheckboxValue;
  autoReinvite: CheckboxValue;
  periodicReinvite: CheckboxValue;
  locale: string;
};

const filterFrequencyOptions = (hidePermanent: boolean) => {
  return hidePermanent ? frequencyOptions.filter((option) => option.value.mode !== "permanent") : frequencyOptions;
};
export const SidecarSettingsModal: React.FC<
  IModalInstance<SidecarSettingsModalResponse> & {
    options?: SidecarSettingsModalProp;
  }
> = observer(({ closeModal, options }) => {
  const sidecarsStore = useDependencyInjector<ISidecarsStore>(InjectSidecarsStore);
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const { t } = useTranslation();
  const filteredFrequencyOptions = filterFrequencyOptions(false);

  const state = useLocalObservable(() => {
    function fromServer(data?: SidecarSettingsModalProp) {
      const currentFrequency = filteredFrequencyOptions.findIndex((option) => isEqual(option.value, data?.runMode));
      const defaultFrequency = filteredFrequencyOptions.findIndex(
        (option: FrequencyOption) =>
          (option.value.mode === "cron" && option.value.cron === sidecarsStore.default_sidecar_frequency) ||
          option.value.mode === sidecarsStore.default_sidecar_frequency
      );
      return {
        frequency: currentFrequency === -1 ? defaultFrequency : currentFrequency,
        notifyEmail: data?.notifyEmail ?? "on",
        autoReinvite: data?.autoReinvite ?? "on",
        periodicReinvite: data?.periodicReinvite ?? "on",
        locale: data?.locale ?? i18n.language,
      };
    }
    const initial = fromServer(options);
    return {
      frequency: initial.frequency,
      notifyEmail: initial.notifyEmail,
      autoReinvite: initial.autoReinvite,
      periodicReinvite: initial.periodicReinvite,
      locale: initial.locale,
      get frequencyItem() {
        return filteredFrequencyOptions[state.frequency] ?? frequencyOptions[3];
      },
      get settings(): SidecarSettingsModalResponse {
        return {
          runMode: state.frequencyItem.value as ClientSideSidecar["runMode"],
          autoReinvite: state.autoReinvite,
          periodicReinvite: state.periodicReinvite,
          notifyEmail: state.notifyEmail,
          locale: state.locale,
        };
      },
      setFrequency: (frequency: number) => {
        state.frequency = frequency;
      },
      setNotifyEmail: (notifyEmail: boolean) => {
        state.notifyEmail = notifyEmail ? "on" : "off";
      },
      setAutoReinvite: (autoReinvite: boolean) => {
        state.autoReinvite = autoReinvite ? "on" : "off";
      },
      setPeriodicReinvite: (periodicReinvite: boolean) => {
        state.periodicReinvite = periodicReinvite ? "on" : "off";
      },
    };
  });

  return (
    <BaseModal closeModal={closeModal} closeOnClickOutside={false} className={styles.settingsModal}>
      {userStore.isAnyAdmin && !options?.hideFrequencyOptions && (
        <div className={styles.selector}>
          <span>{t("sidecars.frequency")}</span>
          <span className={styles.header}>
            <Tooltip
              value={
                <div
                  className={styles.inner}
                  style={{
                    left: `${(state.frequency / (filteredFrequencyOptions.length - 1)) * 100}%`,
                  }}
                >
                  {state.frequencyItem.title}
                </div>
              }
              className={styles.rangeTooltip}
            >
              <input
                type="range"
                min="0"
                max={filteredFrequencyOptions.length - 1}
                value={state.frequency}
                style={{
                  backgroundSize: `${(state.frequency / (filteredFrequencyOptions.length - 1)) * 100}% 100%`,
                }}
                className={styles.slider}
                onChange={(e) => state.setFrequency(parseInt(e.target.value))}
              />
            </Tooltip>
          </span>
        </div>
      )}
      <div className={classnames(styles.selector, styles.field, styles.notify_email)}>
        <CheckBox
          onChange={(e) => state.setNotifyEmail(e.target.checked)}
          icon={state.notifyEmail === "partial" ? "minus-thick" : undefined}
          checked={state.notifyEmail == "on" || state.notifyEmail == "partial"}
          paddingInline={"none"}
          data-testid="notify-email"
          text={t("sidecars.should_notify")}
        />
      </div>
      <div className={classnames(styles.selector, styles.field, styles.notify_email)}>
        <CheckBox
          onChange={(e) => state.setAutoReinvite(e.target.checked)}
          icon={state.autoReinvite === "partial" ? "minus-thick" : undefined}
          checked={state.autoReinvite == "on" || state.autoReinvite == "partial"}
          paddingInline={"none"}
          data-testid="auto-reinvite"
          text={t("sidecars.auto_reinvite")}
        />
      </div>
      <div className={classnames(styles.selector, styles.field, styles.notify_email)}>
        <CheckBox
          onChange={(e) => state.setPeriodicReinvite(e.target.checked)}
          icon={state.periodicReinvite === "partial" ? "minus-thick" : undefined}
          checked={state.periodicReinvite == "on" || state.periodicReinvite == "partial"}
          paddingInline={"none"}
          data-testid="periodic-reinvite"
          text={t("sidecars.periodic_reinvite")}
        />
      </div>
      <ModalButtons
        onSubmit={() => closeModal(state.settings)}
        submitText={options ? t("destinations_page.save") : t("sidecars.invite")}
        onCancel={() => closeModal()}
        testId="submit-sidecar-settings"
      />
    </BaseModal>
  );
});
